import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  BookOutlined,
  CheckCircleOutlined,
  CompassOutlined,
  LockOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import {
  CandidateAnswers,
  CANDIDATE_FINDER_SCORING_VERSION,
  comparisonReason,
  dimensionLabel,
  displayCandidateTitle,
  rankCandidates,
  tierLabel,
} from './scoring'
import {
  captureCandidateFinderAttribution,
  markCandidateFinderSignupIntent,
  trackCandidateFinderEvent,
} from './analytics'
import './BacklistCandidateFinder.css'

type FinderPhase = 'landing' | 'setup' | 'questions' | 'results'
type MarketMode = 'general' | 'specific'
type QuestionKey = Exclude<keyof CandidateAnswers, 'title'>

interface FinderState {
  version: 1
  phase: FinderPhase
  titleCount: 1 | 2 | 3
  marketMode: MarketMode
  targetMarket: string
  questionIndex: number
  candidates: CandidateAnswers[]
}

interface Props {
  isAuth: boolean
  onCreateAccount: () => void
}

interface AnswerOption {
  value: string
  label: string
  detail?: string
}

interface QuestionDefinition {
  key: QuestionKey
  eyebrow: string
  title: string
  help: (state: FinderState) => string
  options: (state: FinderState, candidate: CandidateAnswers) => AnswerOption[]
}

const FINDER_STATE_KEY = 'scribeshadow:backlist-candidate-finder:v1'

const emptyCandidate = (): CandidateAnswers => ({ title: '' })

const initialState = (): FinderState => ({
  version: 1,
  phase: 'landing',
  titleCount: 3,
  marketMode: 'general',
  targetMarket: '',
  questionIndex: 0,
  candidates: [emptyCandidate(), emptyCandidate(), emptyCandidate()],
})

const loadState = (): FinderState => {
  if (typeof window === 'undefined') return initialState()
  try {
    const raw = window.sessionStorage.getItem(FINDER_STATE_KEY)
    if (!raw) return initialState()
    const parsed = JSON.parse(raw) as Partial<FinderState>
    if (
      parsed.version !== 1 ||
      !['landing', 'setup', 'questions', 'results'].includes(String(parsed.phase)) ||
      ![1, 2, 3].includes(Number(parsed.titleCount)) ||
      !Array.isArray(parsed.candidates)
    ) {
      return initialState()
    }
    const titleCount = Number(parsed.titleCount) as 1 | 2 | 3
    const candidates = parsed.candidates
      .slice(0, titleCount)
      .map(candidate => ({ ...(candidate || {}), title: String(candidate?.title || '').slice(0, 80) }))
    while (candidates.length < titleCount) candidates.push(emptyCandidate())
    return {
      version: 1,
      phase: parsed.phase as FinderPhase,
      titleCount,
      marketMode: parsed.marketMode === 'specific' ? 'specific' : 'general',
      targetMarket: String(parsed.targetMarket || '').slice(0, 80),
      questionIndex: Math.max(0, Math.min(QUESTION_STEPS.length - 1, Number(parsed.questionIndex) || 0)),
      candidates,
    }
  } catch (_error) {
    return initialState()
  }
}

const staticOptions = (options: AnswerOption[]) => () => options

const QUESTION_STEPS: QuestionDefinition[] = [
  {
    key: 'rights',
    eyebrow: 'Eligibility',
    title: 'Do you control the rights needed to translate and publish each title?',
    help: state => state.marketMode === 'specific'
      ? `Answer for ${state.targetMarket || 'the language or market you selected'}. This planning tool is not legal advice.`
      : 'Consider translation and territorial publishing rights in the markets you may pursue. This planning tool is not legal advice.',
    options: staticOptions([
      { value: 'controlled', label: 'Yes, I control the necessary rights' },
      { value: 'uncertain', label: 'I am not sure', detail: 'This title will pause for a rights review.' },
      { value: 'unavailable', label: 'No', detail: 'This title will not be scored.' },
    ]),
  },
  {
    key: 'backlistPerformance',
    eyebrow: 'Evidence',
    title: 'How does each title perform within your own backlist?',
    help: () => 'Use relative ranges—no revenue, royalty, or advertising figures are needed.',
    options: staticOptions([
      { value: 'strong', label: 'One of my strongest consistent sellers' },
      { value: 'middle', label: 'Solid middle performer' },
      { value: 'occasional', label: 'Occasional sales' },
      { value: 'rare', label: 'Rarely sells' },
      { value: 'unknown', label: 'Too new or unknown', detail: 'The result will note that evidence is limited.' },
    ]),
  },
  {
    key: 'readerProof',
    eyebrow: 'Evidence',
    title: 'What reader proof does each title have?',
    help: () => 'Think about review patterns and direct reader response, not a single rating or comment.',
    options: staticOptions([
      { value: 'strong', label: 'Strong reviews and clear reader enthusiasm' },
      { value: 'some', label: 'Some positive proof' },
      { value: 'limited', label: 'Limited proof' },
      { value: 'concerns', label: 'Significant recurring reader concerns' },
    ]),
  },
  {
    key: 'bookPosition',
    eyebrow: 'Portfolio leverage',
    title: 'Where does each title sit in its series?',
    help: state => state.marketMode === 'specific'
      ? `For later books, answer based on availability in ${state.targetMarket || 'the selected language or market'}.`
      : 'Without a specific target language, a later series book is treated conservatively because its entry point may not be available.',
    options: state => [
      { value: 'series_opener', label: 'Series opener' },
      { value: 'standalone', label: 'Standalone' },
      ...(state.marketMode === 'specific'
        ? [
            { value: 'later_prior_available', label: 'Later book; preceding books are already available in the target language' },
            { value: 'later_prior_unavailable', label: 'Later book; preceding books are not available in the target language' },
          ]
        : [
            { value: 'later_prior_unavailable', label: 'Later series book', detail: 'Usually the translated entry point should come first.' },
          ]),
    ],
  },
  {
    key: 'seriesPipeline',
    eyebrow: 'Portfolio leverage',
    title: 'What continuation pipeline supports each title?',
    help: () => 'Ready follow-up books can spread launch effort across more than one release.',
    options: (_state, candidate) => candidate.bookPosition === 'standalone'
      ? [{ value: 'standalone', label: 'Standalone—not applicable' }]
      : [
          { value: 'completed', label: 'Completed series or several follow-up books ready' },
          { value: 'next_ready', label: 'The next book is ready' },
          { value: 'planned', label: 'More books are planned but not ready' },
          { value: 'none', label: 'No realistic continuation' },
        ],
  },
  {
    key: 'readThrough',
    eyebrow: 'Evidence',
    title: 'What does existing read-through suggest?',
    help: () => 'A directional assessment is enough; exact percentages are not needed.',
    options: (_state, candidate) => candidate.bookPosition === 'standalone'
      ? [{ value: 'standalone', label: 'Standalone—not applicable' }]
      : [
          { value: 'strong', label: 'Strong' },
          { value: 'moderate', label: 'Moderate' },
          { value: 'weak_unknown', label: 'Weak or unknown' },
        ],
  },
  {
    key: 'packageReadiness',
    eyebrow: 'Execution readiness',
    title: 'How current is the package for each title?',
    help: () => 'Consider whether the cover and metadata still communicate the book accurately and professionally.',
    options: staticOptions([
      { value: 'current', label: 'Cover and metadata are current' },
      { value: 'one_needs_work', label: 'One needs work' },
      { value: 'both_need_work', label: 'Both need work' },
    ]),
  },
  {
    key: 'positioningClarity',
    eyebrow: 'Execution readiness',
    title: 'How clearly can you position each title?',
    help: () => 'Judge the clarity of the genre, intended audience, and central hook.',
    options: staticOptions([
      { value: 'clear', label: 'Genre, audience, and hook are immediately clear' },
      { value: 'mostly_clear', label: 'Mostly clear' },
      { value: 'difficult', label: 'Difficult to explain' },
    ]),
  },
  {
    key: 'marketingCapacity',
    eyebrow: 'Execution readiness',
    title: 'What promotion can you realistically support?',
    help: () => 'This is about available channels and a workable plan—not the size of an advertising budget.',
    options: staticOptions([
      { value: 'specific_plan', label: 'A specific audience and realistic promotion plan' },
      { value: 'some_channels', label: 'Some channels are available' },
      { value: 'no_plan', label: 'No current plan' },
    ]),
  },
  {
    key: 'financialFeasibility',
    eyebrow: 'Execution readiness',
    title: 'How feasible is translation and launch support right now?',
    help: () => 'Use a broad planning judgment. Do not enter an exact budget.',
    options: staticOptions([
      { value: 'can_support', label: 'I can support translation and launch' },
      { value: 'needs_budget', label: 'It needs budgeting, but it is plausible' },
      { value: 'not_feasible', label: 'It is not currently feasible' },
    ]),
  },
]

const tierExplanation = (tier: string): string => {
  if (tier === 'strong') return 'Worth taking into target-market research and a translated sample.'
  if (tier === 'promising') return 'There is potential, but one or more readiness issues should be addressed first.'
  if (tier === 'not_ready') return 'Keep it in the backlist while you strengthen its evidence or launch readiness.'
  if (tier === 'rights_review') return 'Do not proceed until translation and territorial rights are confirmed.'
  return 'Do not translate this title unless the necessary rights become available.'
}

const BacklistCandidateFinder: React.FC<Props> = ({ isAuth, onCreateAccount }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const mainHeadingRef = useRef<HTMLHeadingElement | null>(null)
  const [state, setState] = useState<FinderState>(loadState)
  const [validationMessage, setValidationMessage] = useState('')
  const [continueAfterSignup, setContinueAfterSignup] = useState(false)

  useEffect(() => {
    captureCandidateFinderAttribution(location.search)
  }, [location.search])

  useEffect(() => {
    const previousTitle = document.title
    document.title = 'Which Book Should You Translate First? | ScribeShadow'
    return () => {
      document.title = previousTitle
    }
  }, [])

  useEffect(() => {
    try {
      window.sessionStorage.setItem(FINDER_STATE_KEY, JSON.stringify(state))
    } catch (_error) {
      // The tool remains usable when browser storage is disabled.
    }
  }, [state])

  const visibleCandidates = state.candidates.slice(0, state.titleCount)
  const currentQuestion = QUESTION_STEPS[state.questionIndex]
  const questionCandidates = currentQuestion?.key === 'rights'
    ? visibleCandidates
    : visibleCandidates.filter(candidate => candidate.rights === 'controlled')

  const scoredCandidates = useMemo(
    () => rankCandidates(visibleCandidates),
    [visibleCandidates],
  )
  const eligibleCandidates = scoredCandidates.filter(candidate => candidate.score !== null)
  const leader = eligibleCandidates[0]

  useEffect(() => {
    if (!continueAfterSignup || !isAuth) return
    setContinueAfterSignup(false)
    navigate('/manage-books', {
      state: leader ? { candidateFinderTitle: leader.title } : undefined,
    })
  }, [continueAfterSignup, isAuth, leader, navigate])

  const focusTop = () => {
    window.scrollTo(0, 0)
    window.setTimeout(() => mainHeadingRef.current?.focus(), 0)
  }

  const updateState = (updater: (current: FinderState) => FinderState) => {
    setValidationMessage('')
    setState(updater)
  }

  const setTitleCount = (count: 1 | 2 | 3) => {
    updateState(current => {
      const candidates = current.candidates.slice(0, count)
      while (candidates.length < count) candidates.push(emptyCandidate())
      return { ...current, titleCount: count, candidates }
    })
  }

  const updateTitle = (index: number, title: string) => {
    updateState(current => ({
      ...current,
      candidates: current.candidates.map((candidate, candidateIndex) =>
        candidateIndex === index ? { ...candidate, title: title.slice(0, 80) } : candidate
      ),
    }))
  }

  const updateAnswer = (index: number, key: QuestionKey, value: string) => {
    updateState(current => ({
      ...current,
      candidates: current.candidates.map((candidate, candidateIndex) => {
        if (candidateIndex !== index) return candidate
        const updated: CandidateAnswers = { ...candidate, [key]: value }
        if (key === 'bookPosition') {
          updated.seriesPipeline = undefined
          updated.readThrough = undefined
        }
        return updated
      }),
    }))
  }

  const beginSetup = () => {
    updateState(current => ({ ...current, phase: 'setup' }))
    focusTop()
  }

  const beginQuestions = () => {
    if (state.marketMode === 'specific' && !state.targetMarket.trim()) {
      setValidationMessage('Enter the language or market you are considering.')
      return
    }
    trackCandidateFinderEvent('candidate_finder_started', {
      title_count: state.titleCount,
      market_mode: state.marketMode,
    })
    updateState(current => ({ ...current, phase: 'questions', questionIndex: 0 }))
    focusTop()
  }

  const completeAssessment = () => {
    const ranked = rankCandidates(visibleCandidates)
    const eligible = ranked.filter(candidate => candidate.score !== null)
    trackCandidateFinderEvent('candidate_finder_completed', {
      title_count: state.titleCount,
      eligible_count: eligible.length,
      market_mode: state.marketMode,
      result_tier: eligible[0]?.tier || 'no_eligible_title',
    })
    updateState(current => ({ ...current, phase: 'results' }))
    focusTop()
  }

  const nextQuestion = () => {
    if (!currentQuestion) return
    const missing = questionCandidates.some(candidate => !candidate[currentQuestion.key])
    if (missing) {
      setValidationMessage('Choose an answer for every title shown before continuing.')
      return
    }

    if (currentQuestion.key === 'rights' && questionCandidates.every(candidate => candidate.rights !== 'controlled')) {
      completeAssessment()
      return
    }

    if (state.questionIndex >= QUESTION_STEPS.length - 1) {
      completeAssessment()
      return
    }

    updateState(current => ({ ...current, questionIndex: current.questionIndex + 1 }))
    focusTop()
  }

  const previousStep = () => {
    if (state.questionIndex === 0) {
      updateState(current => ({ ...current, phase: 'setup' }))
    } else {
      updateState(current => ({ ...current, questionIndex: current.questionIndex - 1 }))
    }
    focusTop()
  }

  const restart = () => {
    try {
      window.sessionStorage.removeItem(FINDER_STATE_KEY)
    } catch (_error) {
      // Ignore unavailable storage.
    }
    setValidationMessage('')
    setState(initialState())
    focusTop()
  }

  const editAnswers = () => {
    updateState(current => ({
      ...current,
      phase: 'questions',
      questionIndex: QUESTION_STEPS.length - 1,
    }))
    focusTop()
  }

  const handlePrimaryCta = () => {
    trackCandidateFinderEvent('candidate_finder_cta_clicked', {
      authenticated: isAuth,
      result_tier: leader?.tier || 'no_eligible_title',
    })
    if (isAuth) {
      navigate('/manage-books', {
        state: leader ? { candidateFinderTitle: leader.title } : undefined,
      })
      return
    }
    markCandidateFinderSignupIntent()
    setContinueAfterSignup(true)
    onCreateAccount()
  }

  const renderLanding = () => (
    <main className="candidate-finder-page candidate-finder-landing">
      <section className="candidate-finder-hero" aria-labelledby="candidate-finder-title">
        <div className="candidate-finder-kicker"><CompassOutlined /> Free five-minute planning tool</div>
        <h1 id="candidate-finder-title" ref={mainHeadingRef} tabIndex={-1}>
          Which of Your Books Should You Translate First?
        </h1>
        <p className="candidate-finder-hero-copy">
          Compare up to three backlist titles and identify the strongest candidate to investigate first.
          You will receive a ranked result, the reasons behind it, and the preparation steps that matter.
        </p>
        <div className="candidate-finder-trust-row" aria-label="Tool promises">
          <span><LockOutlined /> No manuscript upload</span>
          <span><SafetyCertificateOutlined /> No sales guarantees</span>
          <span><CheckCircleOutlined /> No email required for results</span>
        </div>
        <button className="candidate-finder-primary-button" type="button" onClick={beginSetup}>
          Compare My Books <ArrowRightOutlined />
        </button>
        <p className="candidate-finder-fine-print">
          This compares your own evidence and readiness. It does not predict sales, return on investment,
          or success in a particular language or country.
        </p>
      </section>
      <section className="candidate-finder-overview" aria-label="What you will receive">
        <article><strong>One clear ranking</strong><span>See which entered title has the strongest current case.</span></article>
        <article><strong>Reasons you can inspect</strong><span>Understand the evidence, leverage, and readiness behind the result.</span></article>
        <article><strong>A practical checklist</strong><span>Leave with no more than three focused preparation tasks per title.</span></article>
      </section>
    </main>
  )

  const renderSetup = () => (
    <main className="candidate-finder-page candidate-finder-workflow">
      <div className="candidate-finder-workflow-header">
        <span className="candidate-finder-kicker">Set up your comparison</span>
        <h1 ref={mainHeadingRef} tabIndex={-1}>Choose the titles to compare</h1>
        <p>Titles are optional and stay in this browser tab. Use Book A, Book B, and Book C if you prefer.</p>
      </div>

      <section className="candidate-finder-panel" aria-labelledby="title-count-heading">
        <h2 id="title-count-heading">How many titles?</h2>
        <div className="candidate-finder-count-options">
          {([1, 2, 3] as const).map(count => (
            <button
              key={count}
              type="button"
              className={state.titleCount === count ? 'is-selected' : ''}
              aria-pressed={state.titleCount === count}
              onClick={() => setTitleCount(count)}
            >
              <strong>{count}</strong>
              <span>{count === 1 ? 'Readiness assessment' : `${count} title comparison`}</span>
              {count === 3 && <em>Recommended</em>}
            </button>
          ))}
        </div>
      </section>

      <section className="candidate-finder-panel" aria-labelledby="candidate-titles-heading">
        <h2 id="candidate-titles-heading">Name your candidates <span>Optional</span></h2>
        <div className="candidate-finder-title-grid">
          {visibleCandidates.map((candidate, index) => (
            <label key={index}>
              <span>Book {index + 1}</span>
              <input
                type="text"
                value={candidate.title}
                maxLength={80}
                placeholder={`Book ${index + 1}`}
                onChange={event => updateTitle(index, event.target.value)}
              />
            </label>
          ))}
        </div>
      </section>

      <section className="candidate-finder-panel" aria-labelledby="market-mode-heading">
        <h2 id="market-mode-heading">Are you already considering a specific language or market?</h2>
        <p>This will only clarify rights and series-order questions; it will not create a market forecast.</p>
        <div className="candidate-finder-choice-grid candidate-finder-choice-grid--compact">
          <button
            type="button"
            className={state.marketMode === 'general' ? 'is-selected' : ''}
            aria-pressed={state.marketMode === 'general'}
            onClick={() => updateState(current => ({
              ...current,
              marketMode: 'general',
              targetMarket: '',
              candidates: current.candidates.map(candidate => candidate.bookPosition === 'later_prior_available'
                ? { ...candidate, bookPosition: undefined }
                : candidate),
            }))}
          >
            <strong>Not yet</strong>
            <span>Compare general backlist readiness first.</span>
          </button>
          <button
            type="button"
            className={state.marketMode === 'specific' ? 'is-selected' : ''}
            aria-pressed={state.marketMode === 'specific'}
            onClick={() => updateState(current => ({ ...current, marketMode: 'specific' }))}
          >
            <strong>Yes</strong>
            <span>Use that context for rights and series availability.</span>
          </button>
        </div>
        {state.marketMode === 'specific' && (
          <label className="candidate-finder-market-input">
            <span>Language or market</span>
            <input
              type="text"
              value={state.targetMarket}
              maxLength={80}
              placeholder="For example: German or Germany"
              onChange={event => updateState(current => ({ ...current, targetMarket: event.target.value.slice(0, 80) }))}
            />
          </label>
        )}
      </section>

      <div className="candidate-finder-validation" role="alert" aria-live="polite">{validationMessage}</div>
      <div className="candidate-finder-navigation">
        <button type="button" className="candidate-finder-secondary-button" onClick={() => updateState(current => ({ ...current, phase: 'landing' }))}>
          <ArrowLeftOutlined /> Back
        </button>
        <button type="button" className="candidate-finder-primary-button" onClick={beginQuestions}>
          Start Assessment <ArrowRightOutlined />
        </button>
      </div>
    </main>
  )

  const renderQuestions = () => {
    if (!currentQuestion) return null
    const progress = Math.round(((state.questionIndex + 1) / QUESTION_STEPS.length) * 100)
    return (
      <main className="candidate-finder-page candidate-finder-workflow">
        <div className="candidate-finder-progress" aria-label={`Step ${state.questionIndex + 1} of ${QUESTION_STEPS.length}`}>
          <div><span>Step {state.questionIndex + 1} of {QUESTION_STEPS.length}</span><span>{progress}%</span></div>
          <div className="candidate-finder-progress-track"><span style={{ width: `${progress}%` }} /></div>
        </div>
        <div className="candidate-finder-workflow-header">
          <span className="candidate-finder-kicker">{currentQuestion.eyebrow}</span>
          <h1 ref={mainHeadingRef} tabIndex={-1}>{currentQuestion.title}</h1>
          <p>{currentQuestion.help(state)}</p>
        </div>

        <div className="candidate-finder-question-candidates">
          {questionCandidates.map(candidate => {
            const index = visibleCandidates.indexOf(candidate)
            const selectedValue = candidate[currentQuestion.key]
            return (
              <fieldset className="candidate-finder-candidate-question" key={index}>
                <legend><BookOutlined /> {displayCandidateTitle(candidate, index)}</legend>
                <div className="candidate-finder-choice-grid">
                  {currentQuestion.options(state, candidate).map(option => (
                    <button
                      key={option.value}
                      type="button"
                      className={selectedValue === option.value ? 'is-selected' : ''}
                      aria-pressed={selectedValue === option.value}
                      onClick={() => updateAnswer(index, currentQuestion.key, option.value)}
                    >
                      <span className="candidate-finder-choice-indicator" aria-hidden="true" />
                      <span><strong>{option.label}</strong>{option.detail && <small>{option.detail}</small>}</span>
                    </button>
                  ))}
                </div>
              </fieldset>
            )
          })}
        </div>

        <div className="candidate-finder-validation" role="alert" aria-live="polite">{validationMessage}</div>
        <div className="candidate-finder-navigation">
          <button type="button" className="candidate-finder-secondary-button" onClick={previousStep}>
            <ArrowLeftOutlined /> Back
          </button>
          <button type="button" className="candidate-finder-primary-button" onClick={nextQuestion}>
            {state.questionIndex === QUESTION_STEPS.length - 1 ? 'See My Results' : 'Continue'} <ArrowRightOutlined />
          </button>
        </div>
        <button type="button" className="candidate-finder-restart-link" onClick={restart}>
          <ReloadOutlined /> Restart comparison
        </button>
      </main>
    )
  }

  const renderResults = () => {
    const hasMultiple = state.titleCount > 1
    const noReadyLeader = leader?.tier === 'not_ready'
    const resultHeading = !leader
      ? 'Rights review comes first'
      : !hasMultiple
        ? `${leader.title}: your readiness assessment`
        : noReadyLeader
          ? `None of these titles is ready yet`
          : `${leader.title} is your strongest candidate to investigate first`

    return (
      <main className="candidate-finder-page candidate-finder-results">
        <section className={`candidate-finder-result-hero candidate-finder-result-hero--${leader?.tier || 'rights'}`}>
          <span className="candidate-finder-kicker">Your backlist assessment</span>
          <h1 ref={mainHeadingRef} tabIndex={-1}>{resultHeading}</h1>
          {leader ? (
            <p>
              {hasMultiple && noReadyLeader
                ? `${leader.title} is the closest of the titles you entered, but important preparation remains.`
                : tierExplanation(leader.tier)}
            </p>
          ) : (
            <p>None of the entered titles can receive a normal recommendation until the necessary rights are confirmed.</p>
          )}
        </section>

        {leader && (
          <section className="candidate-finder-result-card candidate-finder-result-card--featured" aria-labelledby="leading-result-title">
            <div className="candidate-finder-result-card-header">
              <div>
                <span className={`candidate-finder-tier candidate-finder-tier--${leader.tier}`}>{tierLabel(leader.tier)}</span>
                <h2 id="leading-result-title">{leader.title}</h2>
              </div>
              <CheckCircleOutlined aria-hidden="true" />
            </div>

            {leader.dimensions && (
              <div className="candidate-finder-dimensions" aria-label="Assessment dimensions">
                <div><span>Existing evidence</span><strong>{dimensionLabel(leader.dimensions.evidence, 40)}</strong></div>
                <div><span>Portfolio leverage</span><strong>{dimensionLabel(leader.dimensions.leverage, 30)}</strong></div>
                <div><span>Launch readiness</span><strong>{dimensionLabel(leader.dimensions.readiness, 30)}</strong></div>
              </div>
            )}

            {leader.positiveFactors.length > 0 && (
              <div className="candidate-finder-result-section">
                <h3>Why it ranked here</h3>
                <ul>{leader.positiveFactors.map(factor => <li key={factor}>{factor}</li>)}</ul>
              </div>
            )}
            {leader.warnings.length > 0 && (
              <div className="candidate-finder-warning-box">
                <WarningOutlined />
                <div><strong>Watch before proceeding</strong><ul>{leader.warnings.map(warning => <li key={warning}>{warning}</li>)}</ul></div>
              </div>
            )}
            <div className="candidate-finder-result-section">
              <h3>Before translating</h3>
              {leader.preparationTasks.length > 0
                ? <ol>{leader.preparationTasks.map(task => <li key={task}>{task}</li>)}</ol>
                : <p>No urgent preparation issue surfaced. Move next to target-market research and a reviewed sample.</p>}
            </div>
          </section>
        )}

        {scoredCandidates.filter(candidate => candidate !== leader).length > 0 && (
          <section className="candidate-finder-comparison" aria-labelledby="comparison-heading">
            <h2 id="comparison-heading">How the other titles compare</h2>
            <div>
              {scoredCandidates.filter(candidate => candidate !== leader).map(candidate => (
                <article key={candidate.index}>
                  <div><h3>{candidate.title}</h3><span className={`candidate-finder-tier candidate-finder-tier--${candidate.tier}`}>{tierLabel(candidate.tier)}</span></div>
                  <p>{leader ? comparisonReason(candidate, leader) : tierExplanation(candidate.tier)}</p>
                </article>
              ))}
            </div>
          </section>
        )}

        {!leader && (
          <section className="candidate-finder-comparison" aria-labelledby="rights-results-heading">
            <h2 id="rights-results-heading">What to check</h2>
            <div>
              {scoredCandidates.map(candidate => (
                <article key={candidate.index}>
                  <div><h3>{candidate.title}</h3><span className={`candidate-finder-tier candidate-finder-tier--${candidate.tier}`}>{tierLabel(candidate.tier)}</span></div>
                  <p>{tierExplanation(candidate.tier)}</p>
                </article>
              ))}
            </div>
            <p className="candidate-finder-legal-note">This tool provides planning information, not legal advice. Review the relevant contract or consult a qualified adviser.</p>
          </section>
        )}

        <section id="market-research-checklist" className="candidate-finder-market-guide" aria-labelledby="market-guide-heading">
          <div className="candidate-finder-market-guide-heading"><CompassOutlined /><div><span>Next research stage</span><h2 id="market-guide-heading">Test one target-language market</h2></div></div>
          <ol>
            <li><strong>Find comparable books.</strong> Review local retailer categories, covers, descriptions, prices, and reader expectations.</li>
            <li><strong>Validate discoverability.</strong> Check the search terms, retailer access, subscription programs, and promotional channels readers actually use.</li>
            <li><strong>Plan local quality review.</strong> Identify a qualified native-language reviewer and decide how feedback will be incorporated.</li>
            <li><strong>Model the full launch.</strong> Include translation, review, packaging, distribution, promotion, and the next series release—not translation alone.</li>
          </ol>
        </section>

        {leader && (
          <section className="candidate-finder-cta" aria-labelledby="candidate-cta-heading">
            <div>
              <span className="candidate-finder-kicker">Try the next step</span>
              <h2 id="candidate-cta-heading">Test a chapter from {leader.title}</h2>
              <p>
                {isAuth
                  ? 'Add the book to your ScribeShadow library, review the actual credit estimate, and translate a sample when you are ready.'
                  : 'Create a free ScribeShadow account and use your included 5,000 Forever Credits toward a sample. The app will show the actual requirement before translation.'}
              </p>
            </div>
            <button type="button" className="candidate-finder-primary-button" onClick={handlePrimaryCta}>
              {isAuth ? 'Add This Book' : 'Create My Free Account'} <ArrowRightOutlined />
            </button>
          </section>
        )}

        <aside className="candidate-finder-accuracy-note">
          <SafetyCertificateOutlined />
          <p><strong>What this result means:</strong> It compares internal evidence and operational readiness among the titles you entered. It is not a sales forecast, return-on-investment estimate, market analysis, or legal determination.</p>
        </aside>

        <div className="candidate-finder-result-actions">
          <button type="button" className="candidate-finder-secondary-button" onClick={editAnswers}><ArrowLeftOutlined /> Edit answers</button>
          <button type="button" className="candidate-finder-secondary-button" onClick={restart}><ReloadOutlined /> Start over</button>
        </div>
        <p className="candidate-finder-version">Assessment method {CANDIDATE_FINDER_SCORING_VERSION}</p>
      </main>
    )
  }

  if (state.phase === 'landing') return renderLanding()
  if (state.phase === 'setup') return renderSetup()
  if (state.phase === 'questions') return renderQuestions()
  return renderResults()
}

export default BacklistCandidateFinder
